import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import GolfWaitlistModal from '../components/GolfWaitlistModal';
import GoLiveModal from '../components/GoLiveModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };
const cs = (code) => CURRENCY_SYMBOLS[code] || code + ' ';

const PICKS_REQUIRED = 4;
const MIN_COMBINED_ODDS = 120;

const TF_LOGO = 'https://www.targetfunder.com/tf_logo.png';

// Sample prizes shown in demo mode only — identical content to the WC demo
// (DEMO_PRIZES in ClubPredictorPage.js) so clubs see the same teaser regardless
// of which sport they're previewing. Kept in sync manually for now; will DRY
// in the Phase 6 refactor.
const DEMO_PRIZES = [
  { position: 1, title: '1st Place', description: '\u20ac300 Cash Prize', sponsor_name: "Murphy's Bar & Grill", sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/518c71a17cd11ddccf73e05840d6b8afed5d0d12a5ff3019d8633feb7da15fc2.png' },
  { position: 2, title: '2nd Place', description: '\u20ac200 Voucher', sponsor_name: 'Acme Builders Ltd', sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/8b1eb4f8c38d5052b459106dd1d04386bae7c74b8a766dcda32e70abdd41d42d.png' },
  { position: 3, title: '3rd Place', description: '\u20ac100 Gift Card', sponsor_name: 'Village Pharmacy', sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/9db7f47051371bf0c967f92694bee019c8cc4da30e9e93dfdbda4643b888f6c4.png' },
];

// Diagonal "DEMO DEMO DEMO" watermark — identical to the WC demo so
// shareable Golf demo screenshots can't be mistaken for the real product.
function DemoWatermark() {
  return (
    <div className="fixed inset-0 z-[55] pointer-events-none overflow-hidden" aria-hidden="true" data-testid="demo-watermark">
      <div className="absolute inset-0" style={{
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 80px,
          rgba(29, 53, 87, 0.024) 80px,
          rgba(29, 53, 87, 0.024) 82px
        )`,
      }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute whitespace-nowrap select-none"
            style={{
              top: `${(i * 180) - 100}px`,
              left: '-200px',
              right: '-200px',
              transform: 'rotate(-35deg)',
              transformOrigin: 'center center',
              fontSize: '72px',
              fontWeight: 900,
              fontFamily: 'Barlow Condensed, sans-serif',
              letterSpacing: '0.15em',
              color: 'rgba(29, 53, 87, 0.036)',
              textTransform: 'uppercase',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO
          </div>
        ))}
      </div>
    </div>
  );
}

// Colour helpers — duplicated here intentionally (same logic as WC page).
// Keeping them local avoids cross-page coupling for now; will DRY in Phase 6.
const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(255,255,255,${alpha})`;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};
const getContrastColor = (hex) => {
  if (!hex) return '#FFFFFF';
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.4 ? '#0A0A0A' : '#FFFFFF';
};

const OpenLogo = ({ className = '' }) => (
  <img
    src="/assets/the-open-logo.png"
    alt="The Open Championship"
    className={className || 'h-12 sm:h-14 w-auto object-contain'}
    data-testid="open-logo"
  />
);

// Image flag from flagcdn.com — same source as the WC predictor so the demos
// feel cohesive across sports. Handles UK home nations (GB-ENG/SCT/WLS) and
// gracefully degrades to null for unknown codes.
const getFlagUrl = (countryCode) => {
  if (!countryCode || countryCode === 'XX') return null;
  const codeMap = {
    'GB-ENG': 'gb-eng',
    'GB-SCT': 'gb-sct',
    'GB-WLS': 'gb-wls',
    'GB-NIR': 'gb-nir',
  };
  const code = codeMap[countryCode] || countryCode.slice(0, 2).toLowerCase();
  return `https://flagcdn.com/w40/${code}.png`;
};

// Demo activation banner — sits at the top of both pages when demoMode=true.
function GolfDemoBanner({ club, currency, onActivate }) {
  return (
    <div className="sticky top-0 z-[60] bg-[#1D3557] text-white px-6 py-5" data-testid="opengolf-demo-banner">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-bold uppercase tracking-widest bg-[#E63946] px-3 py-3 shrink-0 leading-none">Demo</span>
          <div>
            <p className="text-base sm:text-lg font-bold leading-tight">Like what you see?</p>
            <p className="text-xs sm:text-sm text-white/60">Activate to start raising funds for <strong className="text-white">{club.name}</strong> at The Open 2026</p>
          </div>
        </div>
        <button
          onClick={onActivate}
          className="bg-[#E63946] hover:bg-[#d32f3c] text-white text-sm font-bold uppercase tracking-widest px-8 py-3 transition-colors shrink-0 text-center"
          data-testid="demo-golf-activate-btn"
        >
          <span>Go Live and Start Fundraising →</span>
        </button>
      </div>
    </div>
  );
}

export default function OpenGolfPredictorPage({ demoMode = false, demoClub = null }) {
  const { slug } = useParams();
  const [club, setClub] = useState(demoClub);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(!demoClub);
  const [notFound, setNotFound] = useState(false);

  const [showPicker, setShowPicker] = useState(false);
  const [picks, setPicks] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [playersRes, clubRes] = await Promise.all([
          axios.get(`${API}/golf/players`),
          demoClub ? Promise.resolve({ data: demoClub }) : axios.get(`${API}/golf/clubs/${slug}`),
        ]);
        if (cancelled) return;
        setPlayers(playersRes.data.players || []);
        setClub(clubRes.data);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug, demoClub]);

  // Set browser tab title once the club has loaded. Restore the WC-default
  // on unmount so navigating away (e.g. back to a WC demo) doesn't keep the
  // Golf title.
  useEffect(() => {
    if (!club || !club.name) return undefined;
    const previous = document.title;
    document.title = `${club.name} | Open Golf 2026 Predictor`;
    return () => { document.title = previous; };
  }, [club]);

  const playerById = useMemo(() => {
    const m = {};
    for (const p of players) m[p.id] = p;
    return m;
  }, [players]);

  const combinedOdds = useMemo(
    () => picks.reduce((sum, id) => sum + (playerById[id]?.odds_value || 0), 0),
    [picks, playerById],
  );

  const togglePick = (id) => {
    setError('');
    if (picks.includes(id)) {
      setPicks(picks.filter((x) => x !== id));
      return;
    }
    if (picks.length >= PICKS_REQUIRED) {
      setError(`You can only pick ${PICKS_REQUIRED} golfers — remove one to swap`);
      return;
    }
    setPicks([...picks, id]);
  };

  const visiblePlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => p.name.toLowerCase().includes(q));
  }, [players, search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-sm text-gray-400">Loading…</div>
      </div>
    );
  }
  if (notFound || !club) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center max-w-md">
          <p className="text-xs font-bold uppercase tracking-widest text-[#E63946] mb-2">Open Golf 2026 Predictor</p>
          <h1 className="text-2xl font-black text-[#1D3557] mb-2 font-serif-display">Not found</h1>
          <p className="text-sm text-gray-600">This Open Golf 2026 predictor hasn&apos;t been set up yet. Visit <a href="/" className="underline">targetfunder.com</a> to learn more.</p>
        </div>
      </div>
    );
  }

  const primary = club.primary_color || '#1D3557';
  const secondary = club.secondary_color || '#E63946';
  const textOnPrimary = getContrastColor(primary);
  const symbol = cs(club.currency || 'EUR');
  const currency = club.currency || 'EUR';
  const isValid = picks.length === PICKS_REQUIRED && combinedOdds >= MIN_COMBINED_ODDS;
  const validityReason = picks.length !== PICKS_REQUIRED
    ? `Pick ${PICKS_REQUIRED - picks.length} more`
    : combinedOdds < MIN_COMBINED_ODDS
      ? `Need ${MIN_COMBINED_ODDS}+ odds`
      : null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValid) { setError(validityReason); return; }
    if (!name.trim() || !email.trim()) { setError('Name and email are required'); return; }
    if (demoMode) {
      // In demo mode, "Pay" opens the activation waitlist modal instead of
      // throwing an error. Mirrors how the WC demo handles its payment block.
      setWaitlistOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/golf/clubs/${slug}/entries`, {
        player_name: name.trim(),
        player_email: email.trim(),
        player_phone: phone.trim() || null,
        selected_player_ids: picks,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setError('Could not start checkout — please try again');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────
  // PAGE 1 — Welcome (full-bleed primary-colour hero + bento rules)
  // ──────────────────────────────────────────────────────────────────────
  if (!showPicker) {
    return (
      <div className="min-h-screen bg-white" data-testid="opengolf-welcome">
        {demoMode && <DemoWatermark />}
        {demoMode && <GolfDemoBanner club={club} currency={currency} onActivate={() => setGoLiveOpen(true)} />}
        {demoMode && (
          <GolfWaitlistModal
            open={waitlistOpen}
            onClose={() => setWaitlistOpen(false)}
            defaultClubName={club.name}
            ctaContext="activate_golf_demo"
          />
        )}
        {demoMode && (
          <GoLiveModal
            open={goLiveOpen}
            onClose={() => setGoLiveOpen(false)}
            club={club}
            demoMode
          />
        )}

        {/* Full-bleed hero — saturated club colour. Logos sit prominently
            INSIDE the hero, not in a thin top bar. */}
        <section
          className="relative py-12 sm:py-16 lg:py-20"
          style={{ backgroundColor: primary, color: textOnPrimary }}
          data-testid="opengolf-hero-band"
        >
          <div className="max-w-5xl mx-auto px-6">
            {/* Top eyebrow */}
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-10 sm:mb-14 text-center animate-fade-in-up" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>
              The 154th Open Championship · Royal Birkdale · 16-19 July 2026
            </p>

            {/* Twin logos — large, side-by-side, with elegant divider */}
            <div className="flex items-center justify-center gap-6 sm:gap-12 mb-10 sm:mb-14 animate-fade-in-up delay-1" data-testid="hero-logos">
              <div className="flex flex-col items-center gap-3">
                {club.logo_url ? (
                  <img src={assetUrl(club.logo_url)} alt={club.name} className="w-24 h-24 sm:w-32 sm:h-32 object-contain bg-white rounded-xl p-2 shadow-2xl" />
                ) : (
                  <div className="w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center font-black rounded-xl text-5xl sm:text-6xl bg-white shadow-2xl" style={{ color: primary, fontFamily: 'Playfair Display, Georgia, serif' }}>{club.name.charAt(0)}</div>
                )}
                <div className="text-center leading-tight">
                  <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] mb-1" style={{ color: hexToRgba(textOnPrimary, 0.65) }}>Presents</div>
                  <div className="text-base sm:text-lg font-bold" data-testid="opengolf-club-name">{club.name}</div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-3">
                <div className="w-24 sm:w-32 h-24 sm:h-32 bg-white rounded-xl p-3 sm:p-4 shadow-2xl flex items-center justify-center">
                  <OpenLogo className="w-full h-auto max-h-20 sm:max-h-24 object-contain" />
                </div>
                <div className="text-center leading-tight">
                  <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] mb-1" style={{ color: hexToRgba(textOnPrimary, 0.65) }}>Predict</div>
                  <div className="text-base sm:text-lg font-bold">The Open 2026</div>
                </div>
              </div>
            </div>

            {/* Headline */}
            <div className="text-center">
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight mb-5 animate-fade-in-up delay-2" data-testid="opengolf-hero">
                The Open 2026 Predictor
              </h1>
              <p className="text-base sm:text-lg max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-3" style={{ color: hexToRgba(textOnPrimary, 0.88) }}>
                Pick your 4-man dream team to compete with the best in the world.<br className="hidden sm:block" />Lowest combined score after Sunday wins — bragging rights, prizes, and a club fundraiser rolled into one.
              </p>
            </div>
          </div>
        </section>

        {/* Rules — heavyweight, full-bleed section with club colour as accent */}
        <section className="px-4 sm:px-6 py-14 sm:py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight" style={{ color: primary }}>
                How it works
              </h2>
            </div>

            <div
              className="bg-white rounded-2xl shadow-xl p-6 sm:p-10 lg:p-12 border-t-[6px] animate-fade-in-up"
              style={{ borderTopColor: primary }}
              data-testid="rules-bento"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8 sm:gap-y-10">
                {[
                  { num: 1, title: 'Pick 4', text: <>Choose <strong>4 golfers</strong> from the Open Championship field.</> },
                  { num: 2, title: '120+ odds', text: <>Combined odds across your 4 picks must total <strong>{MIN_COMBINED_ODDS} or more</strong>.</> },
                  { num: 3, title: 'Unique combo', text: <>Each 4-golfer combination is taken on a <strong>first-come basis</strong> — no two entries can have the same 4 players.</> },
                  { num: 4, title: 'Lowest wins', text: <>Lowest combined 4-golfer score after Sunday at Royal Birkdale wins.</> },
                  { num: 5, title: 'Tiebreaker', text: <>If scores tie, the entry with <strong>higher combined odds</strong> wins.</> },
                ].map((rule) => (
                  <div key={rule.num} className="relative pt-3" data-testid={`rule-${rule.num}`}>
                    <span
                      className="font-serif-display absolute top-0 left-0 text-[5rem] font-black select-none pointer-events-none leading-none"
                      style={{ color: hexToRgba(primary, 0.18) }}
                      aria-hidden="true"
                    >
                      {rule.num}
                    </span>
                    <div className="relative z-10 pl-16">
                      <div className="text-sm font-bold uppercase tracking-widest mb-1.5" style={{ color: primary }}>{rule.title}</div>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{rule.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Prizes — real if set, else sample DEMO_PRIZES in demo mode. Mirrors
            WC demo so clubs see the same teaser regardless of sport. */}
        {(() => {
          const realPrizes = club.prizes || [];
          const prizesToShow = realPrizes.length > 0 ? realPrizes : (demoMode ? DEMO_PRIZES : []);
          if (prizesToShow.length === 0) return null;
          // Single-prize layout: centered, larger card so it doesn't sit
          // orphaned in a 3-column grid slot.
          const singular = prizesToShow.length === 1;
          return (
          <section className="px-4 sm:px-6 pb-12 bg-gray-50">
            <div className="max-w-5xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-1 text-center" style={{ color: primary }}>{singular ? 'Prize' : 'Prizes'}</p>
              {demoMode && realPrizes.length === 0 && (
                <p className="text-xs text-gray-400 italic mb-4 text-center">Example prizes — you&apos;ll set your own when you activate</p>
              )}
              <div className={singular ? 'flex justify-center mt-3' : 'grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3'} data-testid="prizes-block">
                {prizesToShow.slice(0, 3).map((p, idx) => {
                  const ribbon = ['1st', '2nd', '3rd'][idx];
                  const title = p.title || p.name || ribbon;
                  const sponsor = p.sponsor_name || p.sponsor || '';
                  return (
                    <div key={idx} className={`bg-white border border-gray-100 rounded-xl text-center ${singular ? 'p-10 sm:p-14 w-full max-w-xl' : 'p-6'}`} data-testid={`prize-${idx + 1}`}>
                      <div className={`font-bold uppercase tracking-widest mb-2 ${singular ? 'text-xl sm:text-2xl' : 'text-sm'}`} style={{ color: primary }}>{title}</div>
                      <p className={`text-gray-800 mb-3 ${singular ? 'text-xl sm:text-2xl leading-snug' : 'text-base'}`}>{p.description || p.label || ''}</p>
                      {p.sponsor_logo_url && (
                        <div className="mt-3 pt-4 border-t border-gray-100">
                          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">Sponsored by</p>
                          <img src={assetUrl(p.sponsor_logo_url)} alt={sponsor || 'Sponsor'} className={`mx-auto object-contain mb-2 ${singular ? 'h-28' : 'h-20'}`} />
                          {sponsor && <p className={`font-bold text-gray-700 ${singular ? 'text-base' : 'text-sm'}`}>{sponsor}</p>}
                        </div>
                      )}
                      {sponsor && !p.sponsor_logo_url && (
                        <div className="mt-3 pt-4 border-t border-gray-100">
                          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Sponsored by</p>
                          <p className={`font-bold text-gray-700 ${singular ? 'text-base' : 'text-sm'}`}>{sponsor}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          );
        })()}

        {/* CTA */}
        <section className="px-6 py-14 bg-gray-50 text-center">
          <button
            type="button"
            onClick={() => { setShowPicker(true); window.scrollTo({ top: 0, behavior: 'instant' }); }}
            className="inline-flex items-center justify-center gap-2 px-10 py-5 text-base sm:text-lg font-bold uppercase tracking-[0.2em] rounded-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl shadow-lg"
            style={{ background: primary, color: textOnPrimary }}
            data-testid="welcome-pick-cta"
          >
            Pick Your 4 Golfers
            <span aria-hidden="true">→</span>
          </button>
          <p className="mt-3 text-xs text-gray-500">Entry {symbol}{Number(club.entry_fee || 10).toFixed(0)} · Takes 60 seconds</p>
        </section>

        {/* Footer — matches WC predictor footer */}
        <footer className="py-6 px-6 border-t border-gray-200 text-center bg-white" data-testid="opengolf-footer">
          <p className="text-gray-500 text-sm">Supporting {club.name} through The Open 2026</p>
          <a
            href="https://www.targetfunder.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 text-xs mt-2 hover:text-gray-600 transition-colors underline"
            data-testid="golf-terms-link"
          >
            Terms &amp; Conditions
          </a>
          <span className="text-gray-300 text-xs mt-2 mx-2">|</span>
          <a
            href="https://www.targetfunder.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 text-xs mt-2 hover:text-gray-600 transition-colors underline"
            data-testid="golf-privacy-link"
          >
            Privacy Notice
          </a>

          <p className="mt-4 text-[11px] text-gray-400 max-w-xl mx-auto">
            The Open Championship name and logos are the property of The R&amp;A.
          </p>

          {/* TargetFunder badge */}
          <div className="mt-5 pt-4 border-t border-gray-200">
            <a
              href="https://www.targetfunder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
              data-testid="targetfunder-badge"
            >
              <img src={TF_LOGO} alt="TargetFunder" className="h-6 w-auto object-contain" />
              <span className="font-heading font-black text-base uppercase tracking-tighter leading-none text-gray-500">
                Made by <span style={{ color: '#1D3557' }}>target</span><span style={{ color: '#E63946' }}>funder</span><span style={{ color: '#1D3557', opacity: 0.3 }}>.com</span>
              </span>
            </a>
          </div>
        </footer>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────
  // PAGE 2 — Picker (flat list + demo overlay)
  // ──────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-44 lg:pb-32" style={{ background: '#fafafa' }} data-testid="opengolf-predictor-page">
      {demoMode && <DemoWatermark />}
      {demoMode && <GolfDemoBanner club={club} currency={currency} onActivate={() => setGoLiveOpen(true)} />}
      {demoMode && (
        <GolfWaitlistModal
          open={waitlistOpen}
          onClose={() => setWaitlistOpen(false)}
          defaultClubName={club.name}
          ctaContext="activate_golf_demo"
        />
      )}
      {demoMode && (
        <GoLiveModal
          open={goLiveOpen}
          onClose={() => setGoLiveOpen(false)}
          club={club}
          demoMode
        />
      )}

      {/* Slim header — club brand + The Open + back link */}
      <header className="bg-white px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 z-40" data-testid="picker-header">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => { setShowPicker(false); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-sm font-bold text-gray-500 hover:text-gray-900 px-2 py-1 -ml-2" data-testid="back-to-welcome">← Back</button>
          {club.logo_url ? (
            <img src={assetUrl(club.logo_url)} alt={club.name} className="w-9 h-9 object-contain" />
          ) : (
            <div className="w-9 h-9 flex items-center justify-center font-bold rounded text-white text-sm" style={{ background: primary }}>{club.name.charAt(0)}</div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold truncate font-serif-display" style={{ color: primary }}>{club.name}</h1>
            <p className="text-[11px] text-gray-500">The Open 2026 · Pick 4 Golfers</p>
          </div>
          <OpenLogo className="h-8 w-auto object-contain hidden sm:block" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT — picker (col-span 2 on desktop) */}
          <div className="lg:col-span-2 relative" data-testid="picker-and-form-wrap">

            {/* Search bar — elegant, underline-only */}
            <div className="mb-5">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search golfer…"
                className="w-full bg-transparent border-0 border-b-2 border-gray-200 focus:outline-none px-1 py-3 text-lg font-serif-display placeholder:text-gray-300 transition-colors"
                style={{ caretColor: primary }}
                onFocus={(e) => e.currentTarget.style.borderBottomColor = primary}
                onBlur={(e) => e.currentTarget.style.borderBottomColor = ''}
                data-testid="player-search"
              />
            </div>

            {/* Single flat player grid — no tier headers */}
            <section data-testid="picker-section">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: primary }}>The Field · {visiblePlayers.length} golfers</h2>
                <span className="text-xs text-gray-500" data-testid="picks-counter">{picks.length} / {PICKS_REQUIRED} picked</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="player-grid">
                {visiblePlayers.map((p) => {
                  const picked = picks.includes(p.id);
                  const dimmed = !picked && picks.length >= PICKS_REQUIRED;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePick(p.id)}
                      className={`text-left p-3 sm:p-4 bg-white border rounded-xl cursor-pointer flex items-center gap-3 transition-all duration-200 ${
                        picked ? 'border-2 shadow-md' : 'border border-gray-200 hover:border-gray-400 hover:-translate-y-0.5'
                      } ${dimmed ? 'opacity-40' : ''}`}
                      style={picked ? { borderColor: primary, backgroundColor: hexToRgba(primary, 0.05) } : {}}
                      data-testid={`player-card-${p.id}`}
                    >
                      {getFlagUrl(p.country_code) ? (
                        <img src={getFlagUrl(p.country_code)} alt="" className="w-7 h-5 object-cover rounded-sm flex-shrink-0 ring-1 ring-black/5" />
                      ) : (
                        <span className="w-7 h-5 bg-gray-100 rounded-sm flex-shrink-0" aria-hidden="true" />
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 truncate">{p.name}</span>
                        <span className="block text-xs text-gray-500 mt-0.5">{p.odds_fractional}</span>
                      </span>
                      {picked && (
                        <span
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ background: primary }}
                          aria-label="Picked"
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mt-4" data-testid="entry-error">{error}</div>
            )}

            {/* Mobile-only "Your Details" — the desktop version lives in the
                right sidebar (hidden on mobile via lg:block). Without this,
                mobile entrants had no visible way to enter name/email so the
                sticky bottom Pay button stayed disabled forever. */}
            <div className="lg:hidden bg-white border border-gray-200 rounded-xl p-5 mt-4" data-testid="your-details-panel-mobile">
              <h3 className="text-base font-black uppercase tracking-widest font-serif-display mb-4" style={{ color: primary }}>Your Details</h3>
              {demoMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts name here</div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts email here</div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts phone here</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" data-testid="entry-name-mobile" required />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                    <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" data-testid="entry-email-mobile" required />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                    <input type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" data-testid="entry-phone-mobile" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-center pt-8 pb-2" data-testid="tf-mid-page">
              <a href="https://www.targetfunder.com" target="_blank" rel="noreferrer noopener">
                <img src={TF_LOGO} alt="Powered by TargetFunder" className="h-5 w-auto grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
              </a>
            </div>
          </div>

          {/* RIGHT — sticky sidebar (desktop only). Mirrors WC demo. */}
          <aside className="lg:col-span-1 hidden lg:block" data-testid="picker-sidebar">
            <div className="sticky top-24 space-y-4">

              {/* My Dream Team */}
              <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="my-dream-team-card">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="text-base font-black uppercase tracking-widest font-serif-display" style={{ color: primary }}>My Dream Team</h3>
                  <span className="text-xs text-gray-500">{picks.length}/{PICKS_REQUIRED}</span>
                </div>

                <div className="space-y-2">
                  {picks.map((id) => {
                    const p = playerById[id];
                    if (!p) return null;
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border-2"
                        style={{ borderColor: primary, backgroundColor: hexToRgba(primary, 0.05) }}
                        data-testid={`dream-team-pick-${id}`}
                      >
                        {getFlagUrl(p.country_code) ? (
                          <img src={getFlagUrl(p.country_code)} alt="" className="w-6 h-4 object-cover rounded-sm flex-shrink-0 ring-1 ring-black/5" />
                        ) : (
                          <span className="w-6 h-4 bg-gray-100 rounded-sm flex-shrink-0" aria-hidden="true" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                          <div className="text-[11px] text-gray-500">{p.odds_fractional}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePick(id)}
                          className="w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                          aria-label={`Remove ${p.name}`}
                          data-testid={`dream-team-remove-${id}`}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>

                {picks.length === 0 && (
                  <p className="text-sm text-gray-400 italic text-center py-3">Pick 4 golfers from the field →</p>
                )}

                {/* Combined odds line */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs uppercase tracking-widest font-bold">Combined odds</span>
                  <span className="font-black font-serif-display" style={{ color: combinedOdds >= MIN_COMBINED_ODDS ? primary : '#9ca3af' }}>
                    {combinedOdds}
                    <span className="text-xs font-normal font-sans text-gray-400 ml-1">/ {MIN_COMBINED_ODDS}</span>
                  </span>
                </div>

                {/* Validation feedback — visible in BOTH demo and live modes when
                    4 picks are made but odds are short of 120. */}
                {picks.length === PICKS_REQUIRED && combinedOdds < MIN_COMBINED_ODDS && (
                  <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs leading-relaxed text-amber-800" data-testid="odds-shortfall-warning">
                    <strong>You need {MIN_COMBINED_ODDS - combinedOdds} more odds.</strong> Try swapping a favourite for a longer shot — combined odds must reach {MIN_COMBINED_ODDS} or more to enter.
                  </div>
                )}
              </div>

              {/* Your Details (demo placeholders OR live inputs) */}
              <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="your-details-panel">
                <h3 className="text-base font-black uppercase tracking-widest font-serif-display mb-4" style={{ color: primary }}>Your Details</h3>
                {demoMode ? (
                  <div className="space-y-3" data-testid="demo-details-preview">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts name here</div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts email here</div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts phone here</div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="entry-name" required />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="entry-email" required />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                      <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="entry-phone" />
                    </div>
                  </div>
                )}
              </div>

              {/* Pay button — coral in demo (NO action; visual only), primary in live */}
              {demoMode ? (
                <div>
                  <button
                    type="button"
                    onClick={(e) => e.preventDefault()}
                    className="w-full px-6 py-3 text-sm font-bold uppercase tracking-widest rounded-lg text-white shadow-md cursor-default"
                    style={{ background: '#FF7F50' }}
                    data-testid="sidebar-demo-pay-btn"
                  >
                    Entrants pay {symbol}{Number(club.entry_fee || 10).toFixed(0)} here
                  </button>
                  <p className="text-[10px] text-gray-400 text-center mt-2">This button goes live when you activate</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || !isValid || !name || !email}
                  className="w-full px-6 py-3 text-sm font-bold uppercase tracking-widest rounded-lg disabled:opacity-40 transition-all duration-300 hover:-translate-y-0.5 shadow-md"
                  style={{ background: isValid ? primary : '#9ca3af', color: textOnPrimary }}
                  data-testid="sidebar-pay-btn"
                >
                  {submitting ? 'Loading…' : isValid ? `Pay ${symbol}${Number(club.entry_fee || 10).toFixed(0)} →` : (validityReason || `Pick ${PICKS_REQUIRED}`)}
                </button>
              )}
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile-only sticky bottom CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]" data-testid="sticky-cta">
        {/* Mobile shortfall warning — only when 4 picks below 120 */}
        {picks.length === PICKS_REQUIRED && combinedOdds < MIN_COMBINED_ODDS && (
          <div className="max-w-5xl mx-auto mb-2 px-3 py-1.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 text-center" data-testid="mobile-odds-shortfall-warning">
            Need <strong>{MIN_COMBINED_ODDS - combinedOdds} more odds</strong> — swap a favourite for a longer shot.
          </div>
        )}
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-bold tracking-widest text-gray-500">Combined odds</div>
            <div className="text-lg font-black font-serif-display" style={{ color: combinedOdds >= MIN_COMBINED_ODDS ? primary : '#9ca3af' }} data-testid="combined-odds-display">
              {combinedOdds}
              <span className="text-xs font-normal font-sans text-gray-500 ml-2">/ {MIN_COMBINED_ODDS} min</span>
            </div>
          </div>
          {demoMode ? (
            <div className="flex flex-col items-end">
              <button
                type="button"
                onClick={(e) => e.preventDefault()}
                className="px-6 sm:px-8 py-3 text-sm sm:text-base font-bold uppercase tracking-widest rounded-lg text-white cursor-default"
                style={{ background: '#FF7F50' }}
                data-testid="demo-pay-here-btn"
              >
                Entrants pay {symbol}{Number(club.entry_fee || 10).toFixed(0)} here
              </button>
              <p className="text-[10px] text-gray-400 mt-1 hidden sm:block">This button goes live when you activate</p>
            </div>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !isValid || !name || !email}
              className="px-6 sm:px-8 py-3 text-sm sm:text-base font-bold uppercase tracking-widest rounded-lg disabled:opacity-40 transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: isValid ? primary : '#9ca3af', color: textOnPrimary }}
              data-testid="entry-submit"
            >
              {submitting ? 'Loading…' : isValid ? `Pay ${symbol}${Number(club.entry_fee || 10).toFixed(0)} →` : (validityReason || `Pick ${PICKS_REQUIRED}`)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
